<script setup lang="ts">
definePageMeta({
  alias: ['/order-giftcard.html']
})

const localeStore = useLanguageStore();
const {addToast} = useToasts();
const {t} = useI18n()
const {isProcessing, startCheckout, paymentError} = usePayment();

const extensions = AllProducts.values().filter(item => item.type === 'Extension').toArray()
const card = AllProducts.values().filter(item => item.type === 'QRCodeEngraved').toArray().at(0)

const selectedExtension = ref(extensions.at(0)?.properties.Duration)
const selectedCard = ref('None')

const totalPrice = computed(() => {
  let total = 0;
  if (selectedExtension.value) {
    const prices = extensions.find(item => item.properties.Duration === selectedExtension.value)?.prices;
    if (prices) {
      total += prices[localeStore.selected.currency]
    }
  }
  if (selectedCard.value === 'Photo' && card) {
    total += card.prices[localeStore.selected.currency]
  }
  return total;
})

const formData = reactive({
  recipientName: '',
  recipientEmail: '',
  clientName: '',
  clientEmail: ''
});

async function doCheckout() {
  const extensionProduct = extensions.find(item => item.properties.Duration === selectedExtension.value);
  const cardProduct = selectedCard.value === 'Photo' ? card : null;
  const requestObject = {
    lang: localeStore.selected.code,
    country: localeStore.selected.region,
    email: formData.clientEmail,
    recipient: {
      name: formData.recipientName,
      email: formData.recipientEmail,
    },
    client: {
      name: formData.clientName,
      email: formData.clientEmail,
    },
    cart: {
      type: CartTypes.GiftCard,
      extensionProduct: extensionProduct,
      cardProduct: cardProduct
    }
  }

  await startCheckout(requestObject, `/success?type=${CartTypes.GiftCard}`, '/order-giftcard');
  if (paymentError.value !== null) {
    console.log(paymentError.value);
    //todo change this
    addToast(t('main.sections.contact.failure.title'), t('main.sections.contact.failure.content'), 'warning');
  }
}
</script>

<template>
  <PageTitle
      :title="$t('orderGiftCards.title')"
      :description="$t('orderGiftCards.description')"
      :links="[
        {name: $t('menu.breadcrumbs.home'), link: '/'},
    ]"
      :current="$t('menu.breadcrumbs.orderGiftCard')"
  />

  <section id="starter-section" class="section">
    <!-- Section Title -->
    <div class="container section-title max-width gift-card">
      <form @submit.prevent="doCheckout">
        <div class="row">
          <div class="col-12 col-md-6 justify-content-center align-items-center d-flex flex-column">
            <label for="extension-option" class="label">{{ $t('orderGiftCards.chooseValidity') }}</label>
            <select class="input-field input-select p-2 m-2" name="person-template" id="extension-option" v-model="selectedExtension">
              <option v-for="extension in extensions" :value=extension.properties.Duration>
                {{ $t(`cartItems.Extension.duration.${extension.properties.Duration}`) }} - {{ extension.prices[localeStore.selected.currency] / 100 }} {{ localeStore.selected.currency }}
              </option>
            </select>
          </div>
          <div class="col-12 col-md-6 justify-content-center align-items-center d-flex flex-column">
            <label for="card-option" class="label">{{ $t('orderGiftCards.includeEngraved') }}</label>
            <select class="input-field input-select p-2 m-2" name="person-template" id="card-option" v-model="selectedCard">
              <option value="None">{{ $t('orderGiftCards.withoutEngraved') }}</option>
              <option value="Photo">{{ $t('orderGiftCards.withEngraved') }} - {{ (card ? card.prices[localeStore.selected.currency] : 0) / 100 }} {{ localeStore.selected.currency }}</option>
            </select>
          </div>
        </div>
        <div class="row pt-4">
          <div class="col-12 col-md-6 justify-content-center align-items-center d-flex flex-column">
            <div class="label">{{ $t('orderGiftCards.recipientDetails') }}</div>
            <div class="input-group-custom col-12 m-2">
              <input v-model="formData.recipientName" type="text" class="input-field" id="recipientName" required placeholder=" ">
              <label for="recipientName" class="input-label">{{ $t('orderGiftCards.recipientName') }}</label>
            </div>

            <div class="input-group-custom col-12 m-2">
              <input v-model="formData.recipientEmail" type="email" class="input-field" id="recipientEmail" required placeholder=" ">
              <label for="recipientEmail" class="input-label">{{ $t('orderGiftCards.recipientEmail') }}</label>
            </div>

          </div>
          <div class="col-12 col-md-6 justify-content-center align-items-center d-flex flex-column">
            <div class="label">{{ $t('orderGiftCards.yourDetails') }}</div>
            <div class="input-group-custom col-12 m-2">
              <input v-model="formData.clientName" type="text" class="input-field" id="clientName" required placeholder=" ">
              <label for="clientName" class="input-label">{{ $t('orderGiftCards.yourName') }}</label>
            </div>
            <div class="input-group-custom col-12 m-2">
              <input v-model="formData.clientEmail" type="email" class="input-field" id="clientEmail" required placeholder=" ">
              <label for="clientEmail" class="input-label">{{ $t('orderGiftCards.yourEmail') }}</label>
            </div>
          </div>
        </div>

        <div class="row d-flex justify-content-center pt-3" id="giftcard-continue-button">
          <LoadingButton class="col-10 col-md-4"
                         type="submit"
                         :loading="isProcessing"
                         :label="$t('checkout.placeOrder') +' - ' + roundUpToTwoDecimals(totalPrice / 100) +' ' +localeStore.selected.currency"
          />
          <div class="py-3 text-muted" v-html="$t('checkout.stripeSecure')"></div>
          <div>
            <img class="payment-logo" src="/assets/img/visa.png" alt="Visa Logo">
            <img class="payment-logo" src="/assets/img/mastercard.png" alt="MasterCard Logo">
            <img class="payment-logo" src="/assets/img/GooglePay.png" alt="Google Pay Logo">
            <img class="payment-logo" src="/assets/img/ApplePay.png" alt="Apple Pay Logo">
          </div>
        </div>
      </form>
    </div><!-- End Section Title -->
  </section><!-- /Section -->
</template>

<style scoped>
section {
  flex-grow: 1;
}
.label {
  color: var(--heading-color);
  font-weight: 700;
}
</style>